import { Button, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers-pro";
import {
  differenceInDays,
  format,
  isBefore,
  isSameDay,
  isValid,
} from "date-fns";
import {
  Controller,
  FieldValues,
  ValidateResult,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ACCUMULATION_OPTIONS } from "../../accumulationOptions";
import { AccumulationType } from "../../types";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../../../common/helpers/formatDateTime";

type Props = {
  accumulationType: AccumulationType;
  searchAccumluations: () => void;
  xsLabel?: number;
  xsField?: number;
  handleAccumulationChange: (value: string) => void;
};

const AccumulationsFields = ({
  accumulationType,
  searchAccumluations,
  xsField,
  xsLabel,
  handleAccumulationChange,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors, isValid: isValidForm },
  } = useFormContext();

  const validateDate = (value: Date): ValidateResult => {
    const isValidDate = !!value && isValid(value) && !isNaN(value.getTime());

    if (!isValidDate) {
      return "Aikajakso sisältää virheellisiä päivämääriä";
    }

    const formatted = format(value, isoFormat);
    const parts = formatted.split("-");
    const yearContainsLeadingZeros = /^0+/.test(parts[0]);

    if (formatted.length !== 10 || yearContainsLeadingZeros) {
      return "Aikajakso sisältää virheellisiä päivämääriä";
    }

    return true;
  };

  const validateDateTo = (
    value: Date,
    formValues: FieldValues,
  ): ValidateResult => {
    const dateResult = validateDate(value);
    if (dateResult !== true) return dateResult;

    const dateFrom = formValues.dateFrom;
    if (dateFrom && isValid(dateFrom)) {
      const oneYearInDays = 365;
      const maxRangeInDays = 5 * oneYearInDays;
      const rangeDurationInDays = differenceInDays(value, dateFrom);

      if (rangeDurationInDays >= 0 && rangeDurationInDays >= maxRangeInDays) {
        return "Aikajakson tulee olla enintään 5 vuotta";
      }

      if (isSameDay(dateFrom, value) || isBefore(value, dateFrom)) {
        return "Aikajakson loppupäivämäärä ei voi olla ennen alkupäivämäärää";
      }
    }

    return true;
  };

  const renderOptions = () => {
    return ACCUMULATION_OPTIONS.filter(
      (option) => option.type === accumulationType,
    )
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((option) => (
        <MenuItem value={option.value}>{option.value}</MenuItem>
      ));
  };

  return (
    <>
      <Grid item xs={xsLabel || 3} alignSelf="center" textAlign="end">
        <Typography variant="secondary">
          {t("talentProfile:accumulation")}
        </Typography>
      </Grid>
      <Grid item xs={xsField || 9}>
        <Controller
          name="accumulation"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value);
                handleAccumulationChange(e.target.value);
              }}
              sx={{ maxWidth: "300px" }}
              size="small"
              fullWidth
              select
            >
              {renderOptions()}
            </TextField>
          )}
        />
      </Grid>
      <Grid item xs={xsLabel || 3} alignSelf="center" textAlign="end">
        <Typography variant="secondary">{t("common:timePeriod")}</Typography>
      </Grid>
      <Grid item container alignItems="center" columnGap={3} xs={xsField || 9}>
        <Controller
          name="dateFrom"
          control={control}
          rules={{
            required: true,
            validate: validateDate,
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              format={leadingZerosDateFormat}
              label={null}
              slotProps={{
                textField: {
                  error: !!errors.dateFrom,
                  helperText: errors.dateFrom?.message?.toString(),
                  size: "small",
                },
              }}
            />
          )}
        />
        <>{t("invoices:separator")}</>
        <Controller
          name="dateTo"
          control={control}
          rules={{
            required: true,
            validate: (value, formValues) => validateDateTo(value, formValues),
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              format={leadingZerosDateFormat}
              label={null}
              slotProps={{
                textField: {
                  error: !!errors.dateTo,
                  helperText: errors.dateTo?.message?.toString(),
                  size: "small",
                },
              }}
            />
          )}
        />
        <Button
          onClick={searchAccumluations}
          disabled={!isValidForm}
          variant="outlined"
          sx={{ borderRadius: "20px" }}
        >
          {t("common:search")}
        </Button>
      </Grid>
    </>
  );
};

export default AccumulationsFields;
